import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Users, Mail, MessageSquare, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

function SegmentPicker({ value, onChange }) {
  const [segments, setSegments] = useState([]);
  useEffect(() => { api.get('/tenant/segments').then(r => setSegments(r.data.segments || [])).catch(() => {}); }, []);
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="mt-2"><SelectValue placeholder="Pilih segment..." /></SelectTrigger>
      <SelectContent>
        {segments.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.customer_count})</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function Broadcast() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    title: '',
    message: '',
    channel: 'email',
    target: 'all'
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchBroadcasts();
  }, []);

  const fetchBroadcasts = async () => {
    try {
      const res = await api.get('/tenant/broadcasts');
      setBroadcasts(res.data.broadcasts || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!form.title || !form.message) {
      toast({ title: 'Error', description: 'Judul dan pesan harus diisi', variant: 'destructive' });
      return;
    }
    
    setSending(true);
    try {
      const res = await api.post('/tenant/broadcasts', form);
      toast({ title: 'Berhasil', description: res.data.message });
      setShowDialog(false);
      setForm({ title: '', message: '', channel: 'email', target: 'all' });
      fetchBroadcasts();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Gagal mengirim', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      completed: { variant: 'default', icon: CheckCircle, label: 'Terkirim', color: 'bg-green-500' },
      sending: { variant: 'secondary', icon: Clock, label: 'Mengirim', color: 'bg-blue-500' },
      pending: { variant: 'outline', icon: Clock, label: 'Terjadwal', color: 'bg-amber-500' },
      failed: { variant: 'destructive', icon: XCircle, label: 'Gagal', color: 'bg-red-500' },
      cancelled: { variant: 'secondary', icon: XCircle, label: 'Dibatalkan', color: '' }
    };
    const s = map[status] || map.pending;
    return <Badge variant={s.variant} className={s.color}>{s.label}</Badge>;
  };

  const getChannelIcon = (channel) => {
    if (channel === 'whatsapp') return <MessageSquare className="h-4 w-4 text-green-600" />;
    if (channel === 'both') return <><Mail className="h-4 w-4 text-blue-600" /><MessageSquare className="h-4 w-4 text-green-600" /></>;
    return <Mail className="h-4 w-4 text-blue-600" />;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="broadcast-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Broadcast</h1>
          <p className="text-muted-foreground">Kirim pesan ke pelanggan Anda</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="create-broadcast-btn">
          <Plus className="h-4 w-4 mr-2" /> Buat Broadcast
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Broadcast</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{broadcasts.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Terkirim</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{broadcasts.filter(b => b.status === 'completed').length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Penerima</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{broadcasts.reduce((sum, b) => sum + (b.sent_count || 0), 0)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Terjadwal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{broadcasts.filter(b => b.status === 'pending').length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Broadcast List */}
      <Card>
        <CardHeader>
          <CardTitle>Riwayat Broadcast</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 font-medium">Judul</th>
                <th className="text-left p-4 font-medium">Channel</th>
                <th className="text-left p-4 font-medium">Target</th>
                <th className="text-left p-4 font-medium">Penerima</th>
                <th className="text-left p-4 font-medium">Status</th>
                <th className="text-left p-4 font-medium">Tanggal</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map(b => (
                <tr key={b.id} className="border-t">
                  <td className="p-4 font-medium">{b.title}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1">
                      {getChannelIcon(b.channel)}
                      <span className="capitalize">{b.channel}</span>
                    </div>
                  </td>
                  <td className="p-4 capitalize">{b.target}</td>
                  <td className="p-4">{b.sent_count || b.recipient_count || 0}</td>
                  <td className="p-4">{getStatusBadge(b.status)}</td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {new Date(b.created_at).toLocaleDateString('id-ID')}
                  </td>
                </tr>
              ))}
              {broadcasts.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    Belum ada broadcast. Klik "Buat Broadcast" untuk mengirim pesan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Buat Broadcast Baru</DialogTitle>
            <DialogDescription>Kirim pesan ke pelanggan Anda melalui email atau WhatsApp</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Judul</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Promo Akhir Tahun" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="both">Keduanya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Target</Label>
                <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="customers">Pelanggan</SelectItem>
                    <SelectItem value="members">Member Aktif</SelectItem>
                    <SelectItem value="affiliates">Affiliate</SelectItem>
                    <SelectItem value="segment">Segment</SelectItem>
                  </SelectContent>
                </Select>
                {form.target === 'segment' && (
                  <SegmentPicker value={form.segment_id} onChange={(v) => setForm({ ...form, segment_id: v })} />
                )}
              </div>
            </div>
          
            <div>
              <Label>Pesan</Label>
              <Textarea 
                value={form.message} 
                onChange={(e) => setForm({ ...form, message: e.target.value })} 
                placeholder="Halo {nama}, kami punya promo spesial untuk Anda..."
                rows={5}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Gunakan {'{nama}'} untuk nama penerima
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Batal</Button>
            <Button onClick={handleSend} disabled={sending}>
              <Send className="h-4 w-4 mr-2" />
              {sending ? 'Mengirim...' : 'Kirim Sekarang'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
